/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Key from '../clientVariables.json';
import { getStripe } from '../utils/stripeClient';

/** CRM must implement: POST /payment/stripe_subscription_payment_intent — creates subscription (incomplete) + returns latest invoice PaymentIntent client_secret. No card data on server. */
const STRIPE_PI_PATH = '/payment/stripe_subscription_payment_intent';

const trail_amount = 1;

function extractClientSecret(res) {
  const payload = res?.data?.data ?? res?.data;
  return (
    payload?.clientSecret ||
    payload?.client_secret ||
    payload?.paymentIntentClientSecret ||
    payload?.payment_intent_client_secret ||
    null
  );
}

function extractSubscriptionPayload(res) {
  const payload = res?.data?.data ?? res?.data;
  return payload?.subscription || payload?.subscriptionId || payload?.subscription_id || null;
}

function StripePaymentInner({ subscriptionForReceipt, ...props }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const saveTransactionAndNotify = useCallback(
    async (paymentIntent, subscriptionPayload) => {
      const receipt = subscriptionPayload || subscriptionForReceipt || paymentIntent;
      const transactionresponse = JSON.stringify(receipt || {});
      const transactionid = receipt;

      const res = await fetch(`${Key.domain}/payment/save_transaction_details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          crm_userid: props.salesPersonDetails.id,
          payment_response: 'succeeded',
          customer_id: props.userDetails.id,
          customer_email: props.userDetails.username,
          transactionid,
          transactionData: transactionresponse,
          companyDetails: props.companyDetails,
          salesPersonDetails: props.salesPersonDetails,
          packageDetails: props.packageDetails,
          userDetails: props.userDetails,
          price:
            parseInt(props?.packageDetails?.trail_period, 10) === 1
              ? trail_amount
              : props.packageDetails.price,
          usertype: props?.userDetails?.usertype,
        }),
      });
      const resdata = await res.json();
      if (resdata?.success === 1) {
        try {
          await axios.post(`${Key.domain}/customer/insert_or_update_subscriptions_trackStatus`, {
            customer_id: props.userDetails.id,
            customer_name: props.userDetails.full_name,
            customer_email: props.userDetails.username,
            current_package_id: props.packageDetails.package_id,
            targeted_package_id: props.packageDetails.package_id,
            status: 1,
            flow_status: 4,
            action_status: 0,
          });
        } catch (e) {
          console.log(e);
        }
        toast.success('Payment Received', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000,
        });
        const chargePayload = subscriptionPayload || subscriptionForReceipt || paymentIntent;
        props.getPaymentResponse('succeeded', {
          transactionresponse: { charge: chargePayload },
        });
      } else {
        toast.error('Something went wrong saving your payment.', {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    },
    [props, subscriptionForReceipt]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const clientSecretParam = params.get('payment_intent_client_secret');
    if (redirectStatus === 'succeeded' && clientSecretParam && stripe) {
      stripe.retrievePaymentIntent(clientSecretParam).then(({ paymentIntent, error }) => {
        if (error) {
          toast.error(error.message || 'Could not verify payment');
          return;
        }
        if (paymentIntent?.status === 'succeeded') {
          window.history.replaceState({}, document.title, window.location.pathname);
          saveTransactionAndNotify(paymentIntent, null);
        }
      });
    }
  }, [stripe, saveTransactionAndNotify]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setMessage(null);
    setSubmitting(true);

    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setMessage(submitErr.message);
      setSubmitting(false);
      return;
    }

    const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    });

    if (error) {
      setMessage(error.message || 'Payment failed');
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      await saveTransactionAndNotify(paymentIntent, null);
    } else if (paymentIntent?.status === 'processing') {
      setMessage('Payment is processing. You will receive confirmation shortly.');
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="card mt-4" style={{ padding: '12px' }}>
        <PaymentElement />
      </div>
      {message && (
        <p className="text-danger mt-2 mb-0" style={{ fontSize: '14px' }}>
          {message}
        </p>
      )}
      <Button
        type="submit"
        style={{ width: '100%' }}
        className="btn btn-primary btn-block mt-4"
        disabled={!stripe || submitting}
      >
        {submitting ? 'Processing…' : 'Pay Now'}
      </Button>
    </form>
  );
}

const Paymentform = (props) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionForReceipt, setSubscriptionForReceipt] = useState(null);
  const [initError, setInitError] = useState(null);
  const [loadingPi, setLoadingPi] = useState(false);
  const stripePromise = getStripe();

  useEffect(() => {
    if (props.packageDetails.Amount === '0' || props.packageDetails.price === '0') return;
    if (!props.packageDetails?.id) return;

    const qs = new URLSearchParams(window.location.search);
    const returnPiSecret = qs.get('payment_intent_client_secret');
    const redirectStatus = qs.get('redirect_status');
    if (returnPiSecret && redirectStatus) {
      setInitError(null);
      setClientSecret(returnPiSecret);
      setSubscriptionForReceipt(null);
      setLoadingPi(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingPi(true);
    setInitError(null);
    setClientSecret(null);
    setSubscriptionForReceipt(null);

    const actualCents = props?.packageDetails?.price
      ? parseInt(props.packageDetails.price, 10) * 100
      : trail_amount * 100;
    const amountCents = trail_amount * 100;

    axios
      .post(`${Key.domain}${STRIPE_PI_PATH}`, {
        subscription_id: props.packageDetails.id,
        actual_amount: actualCents,
        amount: amountCents,
        user_id: props?.userDetails?.id,
        subscription_days: props?.packageDetails?.subscription_days,
        trail_period: props?.packageDetails?.trail_period,
        description: 'Jobs n Profiles Package subscription for' + ' ' + `${props.userDetails.username}`,
        name: props?.userDetails?.full_name,
        email: props?.userDetails?.username,
        usertype: props?.userDetails?.usertype,
      })
      .then((res) => {
        if (cancelled) return;
        const secret = extractClientSecret(res);
        const sub = extractSubscriptionPayload(res);
        if (!secret) {
          setInitError(
            'Payment could not start: server did not return clientSecret. Ensure CRM implements POST ' +
              STRIPE_PI_PATH +
              ' (subscription + PaymentIntent).'
          );
          return;
        }
        setClientSecret(secret);
        if (sub) setSubscriptionForReceipt(sub);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg =
          status === 404
            ? 'Payment upgrade pending: CRM must add POST ' +
              STRIPE_PI_PATH +
              ' returning { clientSecret } for the first invoice PaymentIntent.'
            : err?.response?.data?.message || err?.message || 'Could not start payment';
        setInitError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoadingPi(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    props.packageDetails?.id,
    props.packageDetails?.price,
    props.packageDetails?.Amount,
    props.packageDetails?.trail_period,
    props.userDetails?.id,
    props.userDetails?.username,
  ]);

  const handleFreeConfirm = (event) => {
    event.preventDefault();
    fetch(`${Key.domain}/payment/save_transaction_details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        crm_userid: props.salesPersonDetails.id,
        customer_id: props.userDetails.id,
        customer_email: props.userDetails.username,
        transactionid: '',
        transactionData: '',
        companyDetails: props.companyDetails,
        salesPersonDetails: props.salesPersonDetails,
        packageDetails: props.packageDetails,
        userDetails: props.userDetails,
        price: props.packageDetails.Amount ? props.packageDetails.Amount : props.packageDetails.price,
      }),
    })
      .then((response) => response.json())
      .then((resdata) => {
        props.getPaymentResponse(resdata, null);
      });
  };

  if (props.packageDetails.Amount === '0' || props.packageDetails.price === '0') {
    return (
      <button
        style={{ width: '100%' }}
        type="button"
        className="btn btn-primary btn-block mt-4"
        onClick={handleFreeConfirm}
      >
        Confirm
      </button>
    );
  }

  if (!stripePromise) {
    return (
      <div className="alert alert-danger mt-3" role="alert">
        Stripe publishable key is not set. Add <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> or{' '}
        <code>stripePublishableKey</code> in <code>clientVariables.json</code>.
      </div>
    );
  }

  if (loadingPi && !clientSecret) {
    return (
      <div className="mt-3 text-muted" style={{ textAlign: 'center' }}>
        Loading secure payment form…
      </div>
    );
  }

  if (initError) {
    return (
      <div className="alert alert-danger mt-3" role="alert">
        {initError}
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  return (
    <div>
      <div
        className="bg"
        style={{ backgroundColor: '#0d6efd', padding: '2%', textAlign: 'center', color: '#FFF' }}
      >
        Credit or debit card
      </div>
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: { theme: 'stripe' },
        }}
      >
        <StripePaymentInner {...props} subscriptionForReceipt={subscriptionForReceipt} />
      </Elements>
    </div>
  );
};

export default Paymentform;
